import { useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { TemplateConfiguration, TemplateConfigurationStatus } from '@klareospace/pharos-lib-types';
import { TEMPLATE_CONFIGURATION_STATUS } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createMutationCallbacks } from '@klareospace/pharos-lib-web-utils';

import { createTemplateConfigurationApi, templateConfigurationKeys } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UpdateShipperStatusVariables {
  id: string;
  status: TemplateConfigurationStatus;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getToastKey(status: TemplateConfigurationStatus, outcome: 'error' | 'success'): string {
  if (status === TEMPLATE_CONFIGURATION_STATUS.ACTIVE) {
    return `shippers.toast.resume.${outcome}`;
  }
  if (status === TEMPLATE_CONFIGURATION_STATUS.ARCHIVED) {
    return `shippers.toast.uninstall.${outcome}`;
  }
  return `shippers.toast.pause.${outcome}`;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUpdateShipperStatus(
  onSuccess?: () => void,
): UseMutationResult<TemplateConfiguration, Error, UpdateShipperStatusVariables> {
  const client = useApiClient('deliveries');
  const api = createTemplateConfigurationApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation<TemplateConfiguration, Error, UpdateShipperStatusVariables>({
    mutationFn: ({ id, status }: UpdateShipperStatusVariables) => api.updateTemplateConfigurationStatus(id, { status }),
    ...createMutationCallbacks<TemplateConfiguration, UpdateShipperStatusVariables>({
      errorKey: ({ status }) => getToastKey(status, 'error'),
      invalidateKeys: [templateConfigurationKeys.lists()],
      onSuccess: onSuccess,
      queryClient,
      successKey: ({ status }) => getToastKey(status, 'success'),
      t,
    }),
  });
}
